import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PageBreadcrumbs from "../components/PageBreadcrumbs";
import { useResults } from "../hooks/useResults";
import { useSubmitCompetition } from "../hooks/useSubmitCompetition";
import { STATUS as ResultGroupStatus } from "manager-common";

function ResultGroupsPage() {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { compYear, compid } = useParams();
  const { results, comps, loading, error, refetch } = useResults(compYear, compid);
  const { submitCompetition, loading: submitting } = useSubmitCompetition();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const intervalRef = useRef(null);

  // Find the comp name for the breadcrumb
  const comp = comps.find((c) => c.compId === compid);
  const compName = comp?.showName || compid;

  // Check if competition is already submitted
  const isSubmitted = comp?.submitted || false;
  const submittedAt = comp?.submittedAt;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MM/dd/yyyy HH:mm");
    } catch (err) {
      console.error("Error formatting date:", err);
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case ResultGroupStatus.NEW:
        return "success";
      case ResultGroupStatus.PUBLISHED:
        return "success";
      case ResultGroupStatus.PENDING:
        return "warning";
      case ResultGroupStatus.ERROR:
        return "error";
      case ResultGroupStatus.UPLOADED:
        return "info";
      case ResultGroupStatus.SKIPPED:
        return "default";
      default:
        return "default";
    }
  };

  const toggleRow = (resultId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(resultId)) {
      newExpanded.delete(resultId);
    } else {
      newExpanded.add(resultId);
    }
    setExpandedRows(newExpanded);
  };

  const isRowExpanded = (resultId) => expandedRows.has(resultId);

  // Auto-refresh every 8 seconds continuously while on the page
  useEffect(() => {
    // Set up interval to refresh every 8 seconds
    intervalRef.current = setInterval(() => {
      refetch();
    }, 8000);

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [refetch]);

  const handleSubmitClick = () => {
    setSubmitDialogOpen(true);
    setSubmissionResponse(null);
  };

  const handleCancelSubmit = () => {
    setSubmitDialogOpen(false);
    setSubmissionResponse(null);
  };

  const handleConfirmSubmit = async () => {
    if (!compYear || !compid) {
      setSubmissionResponse({
        error: t("results.submit.errors.missingInfo"),
        details: t("results.submit.errors.missingInfoDetails"),
      });
      return;
    }

    setSubmissionResponse(null);

    try {
      const data = await submitCompetition(compYear, compid);

      setSubmissionResponse({
        success: true,
        message: data.message,
        usefResponse: data.usefResponse,
      });

      // Refresh comps to get updated submission status
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (err) {
      console.error("Error submitting competition:", err);
      setSubmissionResponse({
        error: err.message || t("results.submit.errors.submitError"),
        details: err.details,
      });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <PageBreadcrumbs
          items={[
            { label: t("results.title"), href: "/results" },
            { label: compYear ? `${compName} ${compYear}` : compName },
          ]}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          placeholder={t("results.search.placeholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
          size="small"
        />
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {compYear && compid && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => {
                navigate(`/results/upload`);
              }}
            >
              {t("results.uploadButton")}
            </Button>
          )}
          {compYear && compid && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => {
                const compYearUSEFCompID = `${compYear}#${compid}`;
                navigate(`/results/${compYear}/${compid}/uploads?compYearUSEFCompID=${encodeURIComponent(compYearUSEFCompID)}`);
              }}
              disabled={!compYear || !compid}
            >
              {t("results.uploadHistory.button")}
            </Button>
          )}
          {isSubmitted ? (
            <Typography variant="body2" color="text.secondary">
              {t("results.submit.submittedOn")}{" "}
              {submittedAt ? formatDate(submittedAt) : t("results.submit.unknownDate")}
            </Typography>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleSubmitClick}
              disabled={!compYear || !compid}
            >
              {t("results.submit.button")}
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {(() => {
        // Filter results based on search term
        const filteredResults = [...results].filter((result) => {
          if (!searchTerm.trim()) return true;
          const search = searchTerm.toLowerCase().trim();
          const classId = (result.classId || "").toLowerCase();
          const className = (result.className || "").toLowerCase();
          return classId.includes(search) || className.includes(search);
        });

        // Count skipped results
        const skippedResults = filteredResults.filter(
          (result) =>
            result.status === ResultGroupStatus.SKIPPED ||
            result.className === "(Did not Fill)"
        );
        const skippedCount = skippedResults.length;

        // Filter out skipped results from the display
        const nonSkippedResults = filteredResults.filter(
          (result) =>
            result.status !== ResultGroupStatus.SKIPPED &&
            result.className !== "(Did not Fill)"
        );

        // Sort non-skipped results
        const sortedResults = nonSkippedResults.sort((a, b) => {
          // Sort: ERROR first, then PUBLISHED, then others
          const statusOrder = {
            [ResultGroupStatus.ERROR]: 0,
            [ResultGroupStatus.PUBLISHED]: 1,
          };
          const aOrder = statusOrder[a.status] ?? 3;
          const bOrder = statusOrder[b.status] ?? 3;
          return aOrder - bOrder;
        });

        return (
          <>
            <TableContainer component={Paper}>
              {loading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px"
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("results.columns.classId")}</TableCell>
                      <TableCell>{t("results.columns.className")}</TableCell>
                      <TableCell>{t("results.columns.itemCount")}</TableCell>
                      <TableCell>{t("results.columns.lastUpdate")}</TableCell>
                      <TableCell>{t("results.columns.status")}</TableCell>
                      <TableCell width="48px"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {

                      // Show "no results" message if search returns nothing but there are results
                      if (results.length > 0 && sortedResults.length === 0 && skippedCount === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              {t("results.search.noResults")}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      // Show "empty" message if no results at all
                      if (results.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              {t("results.empty")}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      // Render filtered and sorted results
                      return sortedResults.map((result) => {
                        const isError = result.status === ResultGroupStatus.ERROR;
                        const isPublished = result.status === ResultGroupStatus.PUBLISHED;
                        const isExpanded = isRowExpanded(result.id);

                        return (
                          <React.Fragment key={result.id}>
                            <TableRow
                              hover
                              sx={{
                                "&:last-child td, &:last-child th": { border: 0 },
                              }}
                            >
                              <TableCell>{result.classId}</TableCell>
                              <TableCell>{result.className || "-"}</TableCell>
                              <TableCell>{result.itemCount}</TableCell>
                              <TableCell>{formatDate(result.lastUpdate)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={result.status}
                                  color={getStatusColor(result.status)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {(isError || isPublished) && (
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRow(result.id)}
                                    aria-label="expand row"
                                  >
                                    {isExpanded ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                            {(isError || isPublished) && (
                              <TableRow>
                                <TableCell
                                  style={{ paddingBottom: 0, paddingTop: 0 }}
                                  colSpan={6}
                                >
                                  <Collapse
                                    in={isExpanded}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box sx={{ margin: 2 }}>
                                      {isError ? (
                                        // Error response display
                                        <>
                                          {result.usefResponse?.ResultsResponses &&
                                            Array.isArray(result.usefResponse.ResultsResponses) &&
                                            result.usefResponse.ResultsResponses.length > 0 ? (
                                            <>
                                              <Alert severity="error" sx={{ mb: 1 }}>
                                                <strong>{t("results.responses.errorTitle")}</strong>
                                              </Alert>
                                              {result.usefResponse.ResultsResponses.map(
                                                (response, index) => (
                                                  <Alert
                                                    key={`error-${result.id}-${index}`}
                                                    severity="error"
                                                    sx={{ mb: 1 }}
                                                  >
                                                    <strong>{response.Message}:</strong>{" "}
                                                    {response.Description || t("results.responses.noDescription")}
                                                  </Alert>
                                                )
                                              )}
                                            </>
                                          ) : result.usefResponse ? (
                                            <Alert severity="error" sx={{ mb: 1 }}>
                                              <strong>{t("results.responses.responseTitle")}</strong>
                                              <pre style={{ marginTop: 8, fontSize: "0.875rem" }}>
                                                {JSON.stringify(result.usefResponse, null, 2)}
                                              </pre>
                                            </Alert>
                                          ) : (
                                            <Alert severity="error" sx={{ mb: 1 }}>
                                              {t("results.responses.noErrorDetails")}
                                            </Alert>
                                          )}
                                        </>
                                      ) : isPublished ? (
                                        // Success response display
                                        <>
                                          {result.usefResponse?.ResultsResponses &&
                                            Array.isArray(result.usefResponse.ResultsResponses) &&
                                            result.usefResponse.ResultsResponses.length > 0 ? (
                                            <>
                                              <Alert severity="success" sx={{ mb: 1 }}>
                                                <strong>{t("results.responses.successTitle")}</strong>
                                              </Alert>
                                              {result.usefResponse.ResultsResponses.map(
                                                (response, index) => (
                                                  <Alert
                                                    key={`success-${result.id}-${index}`}
                                                    severity="success"
                                                    sx={{ mb: 1 }}
                                                  >
                                                    <strong>{response.Message}:</strong>{" "}
                                                    {response.Description || t("results.responses.noDescription")}
                                                  </Alert>
                                                )
                                              )}
                                            </>
                                          ) : result.usefResponse ? (
                                            <Alert severity="success" sx={{ mb: 1 }}>
                                              <strong>{t("results.responses.responseTitle")}</strong>
                                              <pre style={{ marginTop: 8, fontSize: "0.875rem" }}>
                                                {JSON.stringify(result.usefResponse, null, 2)}
                                              </pre>
                                            </Alert>
                                          ) : (
                                            <Alert severity="info" sx={{ mb: 1 }}>
                                              {t("results.responses.noResponseDetails")}
                                            </Alert>
                                          )}
                                        </>
                                      ) : null}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
            {skippedCount > 0 && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  opacity: 0.5,
                  fontStyle: "italic",
                }}
              >
                {skippedCount === 1
                  ? t("results.skippedClasses.single", { count: skippedCount })
                  : t("results.skippedClasses.plural", { count: skippedCount })}
              </Typography>
            )}
          </>
        );
      })()}

      {/* Submit Competition Confirmation Dialog */}
      <Dialog
        open={submitDialogOpen}
        onClose={handleCancelSubmit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("results.submit.dialog.title")}</DialogTitle>
        <DialogContent>
          {!submitting && !submissionResponse && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t("results.submit.dialog.warning")}</strong>
              </Typography>
              <Typography variant="body2">
                {t("results.submit.dialog.warningMessage")}
              </Typography>
            </Alert>
          )}
          {submitting && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="100px"
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t("results.submit.dialog.submitting")}
              </Typography>
            </Box>
          )}
          {submissionResponse && (
            <Box>
              {submissionResponse.success ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>{t("results.submit.dialog.success")}</strong>{" "}
                    {submissionResponse.message}
                  </Typography>
                  {submissionResponse.usefResponse?.Message && (
                    <Typography variant="body2">
                      <strong>{t("results.submit.dialog.usefResponse")}</strong>{" "}
                      {submissionResponse.usefResponse.Message}
                      {submissionResponse.usefResponse.Description && (
                        <> - {submissionResponse.usefResponse.Description}</>
                      )}
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>{t("results.submit.dialog.error")}</strong>{" "}
                    {submissionResponse.error}
                  </Typography>
                  {submissionResponse.details && (
                    <Typography variant="body2">
                      {submissionResponse.details}
                    </Typography>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!submitting && !submissionResponse && (
            <>
              <Button onClick={handleCancelSubmit}>
                {t("results.submit.dialog.cancel")}
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                variant="contained"
                color="primary"
              >
                {t("results.submit.dialog.confirm")}
              </Button>
            </>
          )}
          {submissionResponse && (
            <Button
              onClick={() => {
                setSubmitDialogOpen(false);
                setSubmissionResponse(null);
              }}
              variant="contained"
            >
              {t("results.submit.dialog.close")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ResultGroupsPage;
